import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from './cx.ts';

/**
 * Square icon-only button. `label` is mandatory and becomes both the
 * accessible name and the hover title — icon-only affordances must
 * never be unlabeled (DESIGN.md §9).
 */
export type IconButtonSize = 'md' | 'sm';

const SIZE_CLASSES: Record<IconButtonSize, string> = {
  md: 'h-8 w-8',
  sm: 'h-7 w-7',
};

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  readonly label: string;
  readonly size?: IconButtonSize;
  readonly children: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, Props>(function IconButton(
  { label, size = 'md', className, children, type, ...rest },
  ref,
): JSX.Element {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex items-center justify-center rounded text-paper-mid transition-colors duration-120 hover:bg-raised hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 disabled:pointer-events-none disabled:opacity-50',
        SIZE_CLASSES[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
